// Fork: timeline entries are immutable, so note edits arrive as note_revision
// entries. Fold them into their notes for display.
import type { TimelineEntry } from '../../api/types'

export const NOTE_REVISION_TYPE = 'note_revision'

/** Notes added in Regen or /regen, and messages synced from the incident's Slack channel. */
const EDITABLE_NOTE_TYPES = ['message', 'public_update', 'slack_message']

function contentOf(entry: TimelineEntry): Record<string, unknown> {
  return entry.content ?? {}
}

export interface FoldedTimeline {
  /** Entries to display: notes show their latest revision; revisions are removed. */
  entries: TimelineEntry[]
  /** Revisions per original note id, oldest first. */
  revisions: Record<string, TimelineEntry[]>
  /** Each edited note as originally posted. */
  originals: Record<string, TimelineEntry>
}

export function foldNoteRevisions(entries: TimelineEntry[]): FoldedTimeline {
  const revisions: Record<string, TimelineEntry[]> = {}
  for (const entry of entries) {
    if (entry.type !== NOTE_REVISION_TYPE) continue
    const noteId = contentOf(entry).revises
    if (typeof noteId === 'string') (revisions[noteId] ??= []).push(entry)
  }
  for (const list of Object.values(revisions)) {
    list.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  }
  const originals: Record<string, TimelineEntry> = {}
  const folded: TimelineEntry[] = []
  for (const entry of entries) {
    if (entry.type === NOTE_REVISION_TYPE) continue
    const list = revisions[entry.id]
    const latest = list?.[list.length - 1]
    if (!latest) {
      folded.push(entry)
      continue
    }
    originals[entry.id] = entry
    const revision = contentOf(latest)
    // Revisions record public or internal; internal Slack messages stay Slack messages.
    const internalType = entry.type === 'slack_message' ? 'slack_message' : 'message'
    const content: Record<string, unknown> = { ...contentOf(entry) }
    delete content.status_label
    if (typeof revision.status_label === 'string') content.status_label = revision.status_label
    if (typeof revision.note_message === 'string' && revision.note_message) {
      // Edited text replaces both keys: Slack messages render content.text.
      content.message = revision.note_message
      if ('text' in content) content.text = revision.note_message
    }
    folded.push({
      ...entry,
      type: revision.note_type === 'public_update' ? 'public_update' : revision.note_type === 'message' ? internalType : entry.type,
      timestamp: typeof revision.note_timestamp === 'string' ? revision.note_timestamp : entry.timestamp,
      content,
    })
  }
  return { entries: folded, revisions, originals }
}

/** Whether the timeline offers editing for this (folded) entry. */
export function isEditableNote(entry: TimelineEntry, folded: FoldedTimeline): boolean {
  const original = folded.originals[entry.id] ?? entry
  return EDITABLE_NOTE_TYPES.includes(original.type)
}

/** One-line description of a note version, e.g. "Public update · Monitoring". */
export function describeNoteVersion(type: unknown, label: unknown): string {
  const visibility = type === 'public_update' ? 'Public update' : type === 'slack_message' ? 'Slack message' : 'Internal note'
  return typeof label === 'string' && label ? `${visibility} · ${label}` : visibility
}

/**
 * A note's text as readers see it, used to prefill the editor. Slack markup is
 * simplified like the timeline does (Timeline.tsx cleanChatText).
 */
export function readableNoteText(entry: TimelineEntry): string {
  const content = contentOf(entry)
  const message = typeof content.message === 'string' && content.message ? content.message : content.text
  if (typeof message !== 'string') return ''
  return message
    .replace(/<@[A-Z0-9]+\|([^>]+)>/g, '@$1')
    .replace(/<@([A-Z0-9]+)>/g, '@$1')
    .replace(/<#[A-Z0-9]+\|([^>]+)>/g, '#$1')
    .replace(/<(https?:\/\/[^|>]+)\|([^>]+)>/g, '$2')
    .replace(/<(https?:\/\/[^>]+)>/g, '$1')
}
